/**
 * Session-replay gating policy (observability Phase 6 — controlled evaluation).
 *
 * Replay is OFF by default and is NEVER the authoritative clinical record.
 * Policy (docs/observability/clinician-portal-rum-plan.md §3.10):
 *  - allowed only in non-production environments, and only when explicitly
 *    enabled via the SESSION_REPLAY_ENABLED build flag (synthetic patients);
 *  - deny-by-default per route — replay is acceptable ONLY on the small
 *    allowlist of non-clinical, PII-free routes below. Every other route
 *    (patient workspace, patients/clinicians lists, templates, activities,
 *    EMR, settings, session results) is treated as clinical/sensitive.
 *
 * Turning replay on for real also requires enabling it on the CloudWatch RUM
 * app monitor (IaC) after a security review — this module is the client-side
 * guard, not the enable switch.
 */
import { normalizeRoute } from './routeNormalizer';

// Deny-by-default allowlist: only these normalized templates are replay-safe.
export const REPLAY_SAFE_ROUTE_TEMPLATES: readonly string[] = ['/extension', '/privacy'];

/** True only for the explicitly non-clinical, PII-free routes. */
export function isReplayRouteSafe(pathname: string | null | undefined): boolean {
  return REPLAY_SAFE_ROUTE_TEMPLATES.includes(normalizeRoute(pathname).pageTemplate);
}

/**
 * Production never records replay here (non-clinical prod replay is a separate,
 * security-reviewed change).
 */
export function isReplayAllowedInEnvironment(deploymentEnvironment: string): boolean {
  return deploymentEnvironment !== 'production';
}

/**
 * Whether session replay may be enabled at all for this app load: requires the
 * build flag AND a non-production environment. Per-route safety is enforced
 * separately via isReplayRouteSafe.
 */
export function isSessionReplayAllowed(
  deploymentEnvironment: string,
  flagEnabled: boolean,
): boolean {
  return flagEnabled === true && isReplayAllowedInEnvironment(deploymentEnvironment);
}
